/**
 * Helicopter builder — ellipsoid fuselage, tail boom, main rotor disc, tail rotor.
 * Completely separate from fixed-wing geometry.
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import type { ProceduralAircraftParams, CategoryProfile } from '../types'
import { getBodyMaterial, getAccentMaterial, getPropDiscMaterial } from '../materials'

export function createHelicopter(
  params: ProceduralAircraftParams,
  _profile: CategoryProfile
): THREE.Group {
  const group = new THREE.Group()
  group.name = 'procedural-aircraft'

  const lengthM = params.length * FEET_TO_METERS
  const wingspanM = params.wingspan * FEET_TO_METERS // used as rotor diameter
  const heightM = params.height * FEET_TO_METERS

  const bodyMat = getBodyMaterial()
  const accentMat = getAccentMaterial()
  const discMat = getPropDiscMaterial()

  // Fuselage — elongated sphere
  const fuselageLength = lengthM * 0.45 // cabin is ~45% of total length
  const fuselageWidth = lengthM * 0.20
  const fuselageHeight = heightM * 0.45

  const fuselageGeo = new THREE.SphereGeometry(1, 8, 6)
  const fuselage = new THREE.Mesh(fuselageGeo, bodyMat)
  fuselage.scale.set(fuselageWidth / 2, fuselageHeight / 2, fuselageLength / 2)
  fuselage.position.y = fuselageHeight / 2
  fuselage.position.z = lengthM * 0.10 // slightly forward
  fuselage.castShadow = true
  fuselage.receiveShadow = true
  group.add(fuselage)

  // Tail boom — thin cylinder from rear of cabin to tail
  const boomLength = lengthM * 0.55
  const boomRadius = fuselageHeight * 0.12
  const boomGeo = new THREE.CylinderGeometry(boomRadius * 0.5, boomRadius, boomLength, 6)
  const boom = new THREE.Mesh(boomGeo, bodyMat)
  boom.rotation.x = Math.PI / 2 // align along Z
  boom.position.set(0, fuselageHeight * 0.45, -boomLength / 2 + lengthM * 0.05)
  boom.castShadow = true
  group.add(boom)

  // Main rotor mast — short vertical cylinder on top
  const mastHeight = heightM * 0.15
  const mastRadius = boomRadius * 0.8
  const mastGeo = new THREE.CylinderGeometry(mastRadius, mastRadius, mastHeight, 6)
  const mast = new THREE.Mesh(mastGeo, accentMat)
  mast.position.set(0, fuselageHeight + mastHeight / 2, lengthM * 0.08)
  mast.castShadow = true
  group.add(mast)

  // Main rotor disc — flat translucent circle on top
  const rotorRadius = wingspanM / 2 // wingspan field stores rotor diameter for helicopters
  const rotorGeo = new THREE.CircleGeometry(rotorRadius, 24)
  const rotor = new THREE.Mesh(rotorGeo, discMat)
  rotor.rotation.x = -Math.PI / 2 // lay flat
  rotor.position.set(0, fuselageHeight + mastHeight, lengthM * 0.08)
  group.add(rotor)

  // Tail rotor — small vertical disc at end of boom
  const tailRotorRadius = rotorRadius * 0.15
  const tailRotorGeo = new THREE.CircleGeometry(tailRotorRadius, 12)
  const tailRotor = new THREE.Mesh(tailRotorGeo, discMat)
  tailRotor.position.set(
    boomRadius * 0.5 + tailRotorRadius,
    fuselageHeight * 0.50,
    -boomLength + lengthM * 0.08
  )
  group.add(tailRotor)

  // Tail fin — small vertical surface at tail boom end
  const finHeight = tailRotorRadius * 1.5
  const finChord = tailRotorRadius * 2
  const finGeo = new THREE.BoxGeometry(boomRadius * 0.3, finHeight, finChord)
  const fin = new THREE.Mesh(finGeo, bodyMat)
  fin.position.set(
    0,
    fuselageHeight * 0.50 + finHeight / 2,
    -boomLength + lengthM * 0.08
  )
  fin.castShadow = true
  group.add(fin)

  // Landing skids (simplified as two parallel bars)
  const skidLength = fuselageLength * 1.3
  const skidWidth = fuselageWidth * 0.9
  const skidRadius = boomRadius * 0.3
  const skidGeo = new THREE.CylinderGeometry(skidRadius, skidRadius, skidLength, 4)

  for (const side of [-1, 1] as const) {
    const skid = new THREE.Mesh(skidGeo, accentMat)
    skid.rotation.x = Math.PI / 2 // align along Z
    skid.position.set(side * skidWidth / 2, skidRadius, lengthM * 0.05)
    skid.castShadow = true
    group.add(skid)

    // Vertical strut connecting skid to fuselage
    const strutHeight = fuselageHeight / 2 - skidRadius
    const strutGeo = new THREE.CylinderGeometry(skidRadius * 0.5, skidRadius * 0.5, strutHeight, 4)
    const strut = new THREE.Mesh(strutGeo, accentMat)
    strut.position.set(side * skidWidth / 2, skidRadius + strutHeight / 2, lengthM * 0.10)
    strut.castShadow = true
    group.add(strut)
  }

  return group
}
